import * as THREE from 'three/webgpu';
import { float, positionView, uniform, vec3 } from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { MAX_DRONES_PER_PLAYER, MAX_PLAYERS_PER_ROOM, SPAWN_INVULN_MS } from '../../shared/constants';
import { sampleTerrainWorldHeight } from './terrainProfile';
import {
  DEFAULT_VISUAL_QUALITY_PRESET,
  type VisualQualityPreset,
} from './visualConfig';
import {
  DEFAULT_WORLD_THEME,
  type WorldThemeDefinition,
} from './worldTheme';
import {
  uSunDirection as tslUSunDirection,
  buildDroneVertexDisplacement,
  buildDroneEmissive,
  buildDroneColor,
  buildDroneOpacity,
  buildTrailColor,
  buildVaporTrailColor,
  buildShadowColor,
  buildCompressionRingColor,
  buildDataLinkColor,
  buildEcmColor,
  buildEcmSize,
  buildCooldownArcColor,
} from './Shaders';
import { DRONE_ACTIVE_ABILITIES, DroneType } from '../../shared/types';
import { getDroneSpriteVisual, type DroneSpriteVisual } from './droneSpriteAssets';
import { DroneTrails } from './DroneTrails';

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

interface InterpolatedDrone {
  id: string;
  position: Vec3;
  heading: number;
  pitch: number;
  health: number;
  maxHealth: number;
  alive: boolean;
  droneType: number;
  tier: number;
  ownerId: string;
  // Optional — older callers may omit; default 0 (cooldown ready) handled below.
  abilityCooldown?: number;
}

interface DroneMotionState {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  displayRoll: number;
  displayPitch: number;
  lastHeading: number;
  speed: number;
}

interface CompressionRing {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicNodeMaterial;
  /** `uProgress` uniform bound into the ring's `colorNode` — animated from
   * 0 to 1 across `COMPRESSION_RING_DURATION` seconds. */
  progressUniform: ReturnType<typeof uniform>;
  active: boolean;
  progress: number;
}

const MAX_INSTANCES = MAX_DRONES_PER_PLAYER * MAX_PLAYERS_PER_ROOM;
const MAX_VAPOR_INSTANCES = MAX_DRONES_PER_PLAYER * 2;
const COMPRESSION_RING_POOL_SIZE = 4;
const COMPRESSION_RING_DURATION = 0.2;
const COMPRESSION_RING_COOLDOWN = 0.5;
const VAPOR_ROLL_THRESHOLD = 0.2;
const SONIC_SPEED_THRESHOLD = 15.0;
const _forward = new THREE.Vector3(0, 0, 1);
const LOD_CLOSE_DIST_SQ = 44 * 44;
const DRONE_TYPE_COUNT = 6;
const TIER_EMISSIVE_INTENSITY = [0.8, 1.05, 1.4];

/** Duration of the spawn materialization sweep in seconds. */
const SPAWN_MATERIALIZE_SEC = 1.0;
/** Duration of invulnerability in seconds (from shared constants). */
const SPAWN_INVULN_SEC = SPAWN_INVULN_MS / 1000;

interface DroneSpawnState {
  /** 0..1 materialization progress (drives scan-line sweep). */
  spawnProgress: number;
  /** Seconds elapsed since drone first appeared in this lifecycle. */
  age: number;
}

interface DroneSpriteEntry {
  type: number;
  /** Slot index assigned in the per-type InstancedMesh. Used to zero the
   * stale matrix when the entry is removed (see `disposeSpriteEntry`). */
  instanceIndex: number;
  sprite: THREE.Sprite;
  material: THREE.SpriteNodeMaterial;
  glowSprite: THREE.Sprite;
  glowMaterial: THREE.SpriteNodeMaterial;
}

interface HealthBarEntry {
  background: THREE.Sprite;
  backgroundMaterial: THREE.SpriteNodeMaterial;
  fill: THREE.Sprite;
  fillMaterial: THREE.SpriteNodeMaterial;
}

const _scratchBase = new THREE.Color();
const _scratchRed = new THREE.Color(0xff3355);
const _ownColor = new THREE.Color(0x8cf4ff);
const _enemyColor = new THREE.Color(0xff9b88);
const _whiteColor = new THREE.Color(0xffffff);
const _barGreen = new THREE.Color(0x12ff84);
const _barAmber = new THREE.Color(0xffc247);
const _noopRaycast: THREE.Object3D['raycast'] = () => {};
const _activeIds = new Set<string>();
const _seenIds = new Set<string>();
const _scratchColor2 = new THREE.Color();

/** Zero-scale matrix written into a freed InstancedMesh slot on drone
 *  removal so a future slot reuse can't flash the previous occupant's
 *  matrix for a frame before the new write lands. */
const ZERO_MATRIX = new THREE.Matrix4().makeScale(0, 0, 0);

/** Window the sprite stays in the scene after `alive` flips false so the
 *  explosion VFX (fireball + smoke) can overlay the body. */
const DEATH_FX_DURATION_SEC = 0.2;

function decrementTimerMap(timers: Map<string, number>, dt: number): void {
  timers.forEach((remaining, id) => {
    const next = remaining - dt;
    if (next <= 0) timers.delete(id);
    else timers.set(id, next);
  });
}

export class DroneRenderer {
  private readonly droneGroup: THREE.Group;
  private readonly scene: THREE.Scene;
  private readonly qualityPreset: VisualQualityPreset;
  private readonly worldTheme: WorldThemeDefinition;

  private readonly spriteLoader = new THREE.TextureLoader();
  private readonly spriteTextures = new Map<number, THREE.Texture>();
  private readonly droneSprites = new Map<string, DroneSpriteEntry>();
  private readonly healthBars = new Map<string, HealthBarEntry>();
  private readonly previousHealth = new Map<string, number>();
  private readonly hitFlashTimers = new Map<string, number>();
  private readonly critGlowTimers = new Map<string, number>();
  /** Seconds remaining before a dead drone's sprite/health-bar can be
   * disposed. Sprites are kept rendered (with fading opacity) while the
   * entry's value is > 0 so {@link EffectsManager.spawnExplosion} has time
   * to overlay the body. See {@link DEATH_FX_DURATION_SEC}. */
  private readonly deathPendingTimers = new Map<string, number>();

  private readonly instancedMeshes: THREE.InstancedMesh[] = [];
  private readonly instanceDroneIds = Array.from(
    { length: DRONE_TYPE_COUNT },
    () => new Array<string>(MAX_INSTANCES).fill(''),
  );
  private readonly motionStates = new Map<string, DroneMotionState>();
  private readonly spawnStates = new Map<string, DroneSpawnState>();
  /** Last `alive` value observed per drone id. Lets us distinguish a
   * respawning drone (full health, previously seen dead) from a drone joining
   * mid-match (full health, never seen) so the materialize FX replays on
   * respawn. Health alone can't tell them apart — both are at maxHealth. */
  private readonly lastAliveSeen = new Map<string, boolean>();
  private readonly attrSpawnProgress: Float32Array[] = [];
  private readonly attrInvulnerable: Float32Array[] = [];
  private readonly attrIsLocal: Float32Array[] = [];

  private farPoints: THREE.Points;
  private farPointPositions!: Float32Array;
  private farPointColors!: Float32Array;
  private farPointCount = 0;

  private shadowMesh!: THREE.InstancedMesh;
  private trailMesh!: THREE.InstancedMesh;
  private vaporTrailMesh!: THREE.InstancedMesh;
  private closeFxCount = 0;
  private trailCount = 0;
  private vaporTrailCount = 0;

  private compressionRings: CompressionRing[] = [];
  private ringCooldowns = new Map<string, number>();

  private cameraPos = new THREE.Vector3(0, 40, 60);
  private sunDirection = new THREE.Vector3(0.3, 0.9, 0.2);
  private droneMaterial!: THREE.MeshStandardNodeMaterial;
  private time = 0;
  private instanceCounts = new Array(DRONE_TYPE_COUNT).fill(0);

  /* ── C2 Data Links ── */
  private dataLinkMesh!: THREE.LineSegments;
  private dataLinkPositions!: Float32Array;
  private dataLinkColors!: Float32Array;
  private dataLinkDistances!: Float32Array;
  private dataLinkCount = 0;

  /* ── IFF Heartbeat Pulse ── */
  private heartbeatTimer = 0;
  private readonly HEARTBEAT_INTERVAL = 3.0;
  private readonly HEARTBEAT_DURATION = 0.5;
  private swarmCentroid = new THREE.Vector3();
  private maxSwarmRadius = 0;
  private localDronePositions: Float32Array = new Float32Array(MAX_DRONES_PER_PLAYER * 3);
  private localDroneCount = 0;
  private localDroneSpriteIds: string[] = [];

  /* ── Selection rings & leader marker ── */
  private readonly selectionRings = new Map<string, THREE.Mesh>();
  private readonly leaderMarkers = new Map<string, THREE.Mesh>();
  private selectionRingTemplate!: THREE.RingGeometry;
  private selectionRingMaterial!: THREE.MeshBasicMaterial;
  // Replaces the prior gold cone marker. Horizontal flat ring sits just below
  // the leader drone, slightly larger than the body silhouette — visible from
  // above without obscuring the drone itself.
  private leaderMarkerTemplate!: THREE.RingGeometry;
  private leaderMarkerMaterial!: THREE.MeshBasicMaterial;
  private selectedDroneIds = new Set<string>();
  private leaderDroneId = '';

  /* ── Hover state (mouse over a drone) ── */
  /** ID of the drone the mouse is currently hovering over, or '' if none.
   * Drives the rim-glow boost in `addSprite()`. */
  private hoveredDroneId = '';

  /* ── Per-drone ability cooldown arc ── */
  /** Single InstancedMesh for cooldown arcs across all drones. Each slot
   * encodes:
   *   - matrix (position/scale/orientation)  → setMatrixAt
   *   - tint  (per-drone-type accent color)   → setColorAt
   *   - cooldown remaining fraction           → aCooldownFraction attribute
   */
  private cooldownArcMesh!: THREE.InstancedMesh;
  private cooldownArcCount = 0;
  private cooldownArcAttrFraction!: Float32Array;

  /* ── ECM Interference Particles ── */
  private readonly ECM_PARTICLE_COUNT = 18;
  private ecmPoints!: THREE.Points;
  private ecmPositions!: Float32Array;
  private ecmAlphas!: Float32Array;
  private ecmSizes!: Float32Array;
  private ecmPhases: Float32Array;
  private ecmFlickerTimers: Float32Array;
  private ecmFlickerIntervals: Float32Array;
  private ecmFlickerStates: Uint8Array;

  private _matrix = new THREE.Matrix4();
  private _shadowMatrix = new THREE.Matrix4();
  private _position = new THREE.Vector3();
  private _quaternion = new THREE.Quaternion();
  private _scale = new THREE.Vector3(1, 1, 1);
  private _shadowScale = new THREE.Vector3(1, 1, 1);
  private _euler = new THREE.Euler();
  private _shadowEuler = new THREE.Euler(-Math.PI / 2, 0, 0);
  private _shadowQuaternion = new THREE.Quaternion().setFromEuler(this._shadowEuler);
  private _color = new THREE.Color();
  private _trailOffset = new THREE.Vector3();
  private _shadowPos = new THREE.Vector3();
  private _wingtipOffset = new THREE.Vector3();
  private _ringDir = new THREE.Vector3();
  private _scratchVec3 = new THREE.Vector3();
  private _scratchColor3 = new THREE.Color();
  private _ringQuat = new THREE.Quaternion();

  /** Subtle motion-trail particles for fast-moving drones. Reads positions /
   * velocities directly from `motionStates` each frame. */
  private droneTrails!: DroneTrails;

  constructor(
    scene: THREE.Scene,
    qualityPreset: VisualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET,
    worldTheme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
  ) {
    this.scene = scene;
    this.qualityPreset = qualityPreset;
    this.worldTheme = worldTheme;

    this.ecmPhases = new Float32Array(this.ECM_PARTICLE_COUNT);
    this.ecmFlickerTimers = new Float32Array(this.ECM_PARTICLE_COUNT);
    this.ecmFlickerIntervals = new Float32Array(this.ECM_PARTICLE_COUNT);
    this.ecmFlickerStates = new Uint8Array(this.ECM_PARTICLE_COUNT);
    for (let i = 0; i < this.ECM_PARTICLE_COUNT; i++) {
      this.ecmPhases[i] = Math.random() * Math.PI * 2;
      this.ecmFlickerTimers[i] = Math.random() * 0.3;
      this.ecmFlickerIntervals[i] = 0.05 + Math.random() * 0.25;
      this.ecmFlickerStates[i] = Math.random() > 0.5 ? 1 : 0;
    }

    this.droneGroup = new THREE.Group();
    scene.add(this.droneGroup);
    this.initInstancedMeshes();
    this.initCloseEffects();
    this.initDataLinks();
    this.initECM();
    this.initSelectionVisuals();
    this.initCooldownArc();
    this.farPoints = this.initFarPoints();
    // Drone motion trails — pooled additive Points emitter that consumes
    // the per-drone position/velocity already tracked in `motionStates`.
    this.droneTrails = new DroneTrails(scene, qualityPreset);
  }

  /** Update which drones are visually marked as selected / leader.
   * Rings/markers materialize on first reference and are reused across frames. */
  setSelection(selectedIds: string[], leaderId: string): void {
    this.selectedDroneIds.clear();
    for (const id of selectedIds) this.selectedDroneIds.add(id);
    this.leaderDroneId = leaderId;
  }

  /**
   * Update which drone the cursor is hovering over. Pass `''` (or `null`) to
   * clear the hover state. Called by `InputManager` from a throttled
   * raycaster — `DroneRenderer` itself does no raycasting.
   */
  setHoveredDrone(droneId: string | null): void {
    this.hoveredDroneId = droneId ?? '';
  }

  /** Read-only accessor for tests and external diagnostics. */
  getHoveredDrone(): string {
    return this.hoveredDroneId;
  }

  private initSelectionVisuals(): void {
    // Cyan additive ring sits just below each selected drone.
    this.selectionRingTemplate = new THREE.RingGeometry(2.6, 3.1, 36);
    this.selectionRingTemplate.rotateX(-Math.PI / 2);
    this.selectionRingMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      toneMapped: false,
    });

    // Gold horizontal halo ring sits at the leader drone's base. Slightly
    // larger than the body bounding box (~3.6 unit silhouette), low opacity
    // so it reads from above without obstructing the drone. Replaces the
    // older downward-pointing cone, which obscured the drone in top-down view.
    this.leaderMarkerTemplate = new THREE.RingGeometry(3.4, 3.9, 48);
    this.leaderMarkerTemplate.rotateX(-Math.PI / 2);
    this.leaderMarkerMaterial = new THREE.MeshBasicMaterial({
      color: 0xffd24d,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
  }

  private getOrCreateSelectionRing(id: string): THREE.Mesh {
    let ring = this.selectionRings.get(id);
    if (!ring) {
      ring = new THREE.Mesh(this.selectionRingTemplate, this.selectionRingMaterial);
      ring.frustumCulled = false;
      ring.raycast = _noopRaycast;
      ring.userData.selectionRing = true;
      this.droneGroup.add(ring);
      this.selectionRings.set(id, ring);
    }
    return ring;
  }

  private getOrCreateLeaderMarker(id: string): THREE.Mesh {
    let marker = this.leaderMarkers.get(id);
    if (!marker) {
      marker = new THREE.Mesh(this.leaderMarkerTemplate, this.leaderMarkerMaterial);
      marker.frustumCulled = false;
      marker.raycast = _noopRaycast;
      marker.userData.leaderMarker = true;
      this.droneGroup.add(marker);
      this.leaderMarkers.set(id, marker);
    }
    return marker;
  }

  /**
   * Sets up the per-drone cooldown arc — a single thin ring InstancedMesh
   * (max one slot per active drone). Each frame the arc material reads the
   * `aCooldownFraction` per-instance attribute (cooldown remaining ÷ max)
   * and the per-instance color (drone-type accent) and renders a partial
   * arc. When `aCooldownFraction === 0`, the ring is fully filled.
   */
  private initCooldownArc(): void {
    const ringGeo = new THREE.RingGeometry(2.9, 3.25, 64);
    ringGeo.rotateX(-Math.PI / 2);

    const ringMat = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    ringMat.colorNode = buildCooldownArcColor();

    const mesh = new THREE.InstancedMesh(ringGeo, ringMat, MAX_INSTANCES);
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.raycast = _noopRaycast;
    mesh.userData.cooldownArcMesh = true;

    this.cooldownArcAttrFraction = new Float32Array(MAX_INSTANCES);
    ringGeo.setAttribute(
      'aCooldownFraction',
      new THREE.InstancedBufferAttribute(this.cooldownArcAttrFraction, 1),
    );

    this.cooldownArcMesh = mesh;
    this.droneGroup.add(mesh);
  }

  /**
   * Per-drone-type accent color used for the cooldown arc. Picked to match
   * the existing tier emissive palette without depending on player team
   * colors (which already drive selection rings and rim lighting).
   */
  private getCooldownArcAccent(droneType: number): THREE.Color {
    switch (droneType) {
      case DroneType.Striker:     return _scratchColor2.setRGB(1.0, 0.55, 0.30);
      case DroneType.Sniper:      return _scratchColor2.setRGB(0.45, 0.80, 1.0);
      case DroneType.Healer:      return _scratchColor2.setRGB(0.40, 1.0, 0.55);
      case DroneType.Interceptor: return _scratchColor2.setRGB(1.0, 0.85, 0.30);
      case DroneType.Support:     return _scratchColor2.setRGB(0.65, 0.50, 1.0);
      case DroneType.Guardian:    return _scratchColor2.setRGB(1.0, 0.35, 0.45);
      default:                    return _scratchColor2.setRGB(0.85, 0.90, 1.0);
    }
  }

  /**
   * Push a cooldown arc instance for the given drone. Called once per drone
   * per frame from the main update loop.
   */
  private addCooldownArc(drone: InterpolatedDrone, motion: DroneMotionState): void {
    const idx = this.cooldownArcCount;
    if (idx >= MAX_INSTANCES) return;
    this.cooldownArcCount = idx + 1;

    const droneType = Math.min(Math.max(drone.droneType, 0), DRONE_TYPE_COUNT - 1);
    const cooldownMax = DRONE_ACTIVE_ABILITIES[droneType as DroneType]?.cooldownMs ?? 0;
    const remaining = drone.abilityCooldown ?? 0;
    const fraction = cooldownMax > 0
      ? Math.min(1, Math.max(0, remaining / cooldownMax))
      : 0;
    this.cooldownArcAttrFraction[idx] = fraction;

    // Sit just under the drone — slightly below the selection ring so the two
    // can coexist without z-fighting.
    this._shadowPos.set(motion.position.x, motion.position.y - 1.7, motion.position.z);
    this._shadowScale.set(1, 1, 1);
    this._shadowMatrix.compose(this._shadowPos, this._shadowQuaternion, this._shadowScale);
    this.cooldownArcMesh.setMatrixAt(idx, this._shadowMatrix);
    this.cooldownArcMesh.setColorAt(idx, this.getCooldownArcAccent(droneType));
  }

  private updateSelectionVisuals(): void {
    this.selectionRings.forEach((ring) => { ring.visible = false; });
    this.leaderMarkers.forEach((marker) => { marker.visible = false; });

    const time = this.time;

    for (const id of this.selectedDroneIds) {
      const motion = this.motionStates.get(id);
      if (!motion) continue;
      const ring = this.getOrCreateSelectionRing(id);
      ring.position.set(motion.position.x, motion.position.y - 1.4, motion.position.z);
      const pulse = 1 + Math.sin(time * 4) * 0.04;
      ring.scale.set(pulse, pulse, pulse);
      ring.visible = true;
    }

    if (this.leaderDroneId) {
      const motion = this.motionStates.get(this.leaderDroneId);
      if (motion) {
        const marker = this.getOrCreateLeaderMarker(this.leaderDroneId);
        // Sit the halo just below the body so it reads as a base ring rather
        // than an overhead crown. Subtle vertical bob preserves a sense of
        // motion. The slow yaw keeps additive sparkle aliasing from looking
        // static when the drone is stationary.
        const bob = Math.sin(time * 3) * 0.25;
        marker.position.set(motion.position.x, motion.position.y - 1.0 + bob * 0.2, motion.position.z);
        marker.rotation.y = time * 0.6;
        // Gentle pulse keeps it visible without screaming.
        const haloPulse = 1 + Math.sin(time * 3) * 0.04;
        marker.scale.set(haloPulse, haloPulse, haloPulse);
        marker.visible = true;
      }
    }
  }

  setCameraPosition(pos: Vec3): void {
    this.cameraPos.set(pos.x, pos.y, pos.z);
  }

  setSunDirection(dir: Vec3): void {
    this.sunDirection.set(dir.x, dir.y, dir.z).normalize();
  }

  pulseCritGlow(droneId: string, durationMs = 200): void {
    if (!droneId) return;
    const durationSec = Math.max(0, durationMs / 1000);
    if (durationSec <= 0) return;
    this.critGlowTimers.set(droneId, Math.max(this.critGlowTimers.get(droneId) ?? 0, durationSec));
  }

  update(drones: InterpolatedDrone[], localPlayerId: string, deltaMs: number = 16): void {
    const dt = Math.max(deltaMs / 1000, 1 / 240);
    this.time += dt;

    // Module-scoped TSL uniforms feed the drone body `emissiveNode`,
    // `positionNode`, `colorNode`, and the trail / datalink / ring builders.
    (tslUSunDirection.value as THREE.Vector3).copy(this.sunDirection);

    const counts = this.instanceCounts;
    for (let i = 0; i < DRONE_TYPE_COUNT; i++) counts[i] = 0;
    this.farPointCount = 0;
    this.closeFxCount = 0;
    this.trailCount = 0;
    this.vaporTrailCount = 0;
    this.cooldownArcCount = 0;

    this.localDroneCount = 0;
    this.localDroneSpriteIds.length = 0;
    this.healthBars.forEach((entry) => {
      entry.background.visible = false;
      entry.fill.visible = false;
    });

    decrementTimerMap(this.hitFlashTimers, dt);
    decrementTimerMap(this.critGlowTimers, dt);
    // Decrement up front so handleDeathPending() reads the current remaining
    // time, not last frame's pre-decrement value.
    decrementTimerMap(this.deathPendingTimers, dt);

    this.heartbeatTimer += dt;
    if (this.heartbeatTimer >= this.HEARTBEAT_INTERVAL) {
      this.heartbeatTimer -= this.HEARTBEAT_INTERVAL;
    }

    _activeIds.clear();
    _seenIds.clear();

    for (const drone of drones) {
      _seenIds.add(drone.id);
      if (!drone.alive) {
        const wasAlive = this.lastAliveSeen.get(drone.id) === true;
        if (wasAlive && this.droneSprites.has(drone.id)) {
          // alive→dead transition — start the death-fade window.
          this.deathPendingTimers.set(drone.id, DEATH_FX_DURATION_SEC);
        }
        this.lastAliveSeen.set(drone.id, false);
        if ((this.deathPendingTimers.get(drone.id) ?? 0) > 0) {
          this.handleDeathPending(drone);
        }
        continue;
      }
      _activeIds.add(drone.id);
      this.deathPendingTimers.delete(drone.id);

      this._position.set(drone.position.x, drone.position.y, drone.position.z);
      const distSq = this._position.distanceToSquared(this.cameraPos);
      const isOwn = drone.ownerId === localPlayerId;
      const healthFrac = drone.maxHealth > 0 ? drone.health / drone.maxHealth : 1;
      const prevHealth = this.previousHealth.get(drone.id);
      if (prevHealth !== undefined && drone.health < prevHealth - 0.01) {
        this.hitFlashTimers.set(drone.id, 0.28);
      }
      this.previousHealth.set(drone.id, drone.health);
      const motion = this.updateMotionState(drone, dt);

      if (isOwn) {
        const off = this.localDroneCount * 3;
        this.localDronePositions[off] = this._position.x;
        this.localDronePositions[off + 1] = this._position.y;
        this.localDronePositions[off + 2] = this._position.z;
        this.localDroneCount++;
      }

      this.addSprite(drone, motion, isOwn, healthFrac);
      this.updateHealthBar(drone, isOwn, healthFrac);

      // Cooldown arc — only render for the local player's drones, since the
      // ring is intended as a HUD aid for active loadout management. Showing
      // it for enemy drones would clutter the scene and leak info.
      if (isOwn) {
        this.addCooldownArc(drone, motion);
      }

      if (distSq < LOD_CLOSE_DIST_SQ) {
        this.addCloseEffects(drone, motion, isOwn, healthFrac);
      }
    }

    // Drop death-pending entries for drones that have fully left the snapshot.
    this.deathPendingTimers.forEach((_remaining, id) => {
      if (!_seenIds.has(id)) this.deathPendingTimers.delete(id);
    });

    const isStillVisible = (id: string) =>
      _activeIds.has(id) || this.deathPendingTimers.has(id);

    // Cleared on the alive→dead frame so updateMotionState's `wasDead` check
    // fires on respawn (motionState absence is the transition signal).
    this.motionStates.forEach((_state, id) => {
      if (!_activeIds.has(id)) this.motionStates.delete(id);
    });

    this.ringCooldowns.forEach((_v, id) => {
      if (!_activeIds.has(id)) this.ringCooldowns.delete(id);
    });

    this.droneSprites.forEach((entry, id) => {
      if (!isStillVisible(id)) {
        this.disposeSpriteEntry(entry);
        this.droneSprites.delete(id);
      }
    });
    this.healthBars.forEach((entry, id) => {
      if (!isStillVisible(id)) {
        this.disposeHealthBar(entry);
        this.healthBars.delete(id);
      }
    });
    this.previousHealth.forEach((_health, id) => {
      if (!_activeIds.has(id)) this.previousHealth.delete(id);
    });
    this.hitFlashTimers.forEach((_remaining, id) => {
      if (!_activeIds.has(id)) this.hitFlashTimers.delete(id);
    });
    this.critGlowTimers.forEach((_remaining, id) => {
      if (!_activeIds.has(id)) this.critGlowTimers.delete(id);
    });

    this.selectionRings.forEach((ring, id) => {
      if (!_activeIds.has(id)) {
        this.droneGroup.remove(ring);
        this.selectionRings.delete(id);
      }
    });
    this.leaderMarkers.forEach((marker, id) => {
      if (!_activeIds.has(id)) {
        this.droneGroup.remove(marker);
        this.leaderMarkers.delete(id);
      }
    });

    this.spawnStates.forEach((ss, id) => {
      if (!_activeIds.has(id)) {
        this.spawnStates.delete(id);
      } else {
        ss.age += dt;
        ss.spawnProgress = Math.min(1, ss.age / SPAWN_MATERIALIZE_SEC);
      }
    });

    this.lastAliveSeen.forEach((_v, id) => {
      if (!_seenIds.has(id)) this.lastAliveSeen.delete(id);
    });

    for (let t = 0; t < DRONE_TYPE_COUNT; t++) {
      const mesh = this.instancedMeshes[t];
      mesh.count = counts[t];
      if (counts[t] > 0) {
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        const geo = mesh.geometry;
        const spAttr = geo.getAttribute('aSpawnProgress');
        const invAttr = geo.getAttribute('aInvulnerable');
        const locAttr = geo.getAttribute('aIsLocal');
        if (spAttr) (spAttr as THREE.BufferAttribute).needsUpdate = true;
        if (invAttr) (invAttr as THREE.BufferAttribute).needsUpdate = true;
        if (locAttr) (locAttr as THREE.BufferAttribute).needsUpdate = true;
      }
    }

    this.shadowMesh.count = this.qualityPreset.features.blobShadows ? this.closeFxCount : 0;
    this.trailMesh.count = this.trailCount;
    if (this.qualityPreset.features.blobShadows && this.closeFxCount > 0) {
      this.shadowMesh.instanceMatrix.needsUpdate = true;
      if (this.shadowMesh.instanceColor) this.shadowMesh.instanceColor.needsUpdate = true;
    }
    if (this.trailCount > 0) {
      this.trailMesh.instanceMatrix.needsUpdate = true;
      if (this.trailMesh.instanceColor) this.trailMesh.instanceColor.needsUpdate = true;
    }

    this.vaporTrailMesh.count = this.vaporTrailCount;
    if (this.vaporTrailCount > 0) {
      this.vaporTrailMesh.instanceMatrix.needsUpdate = true;
      if (this.vaporTrailMesh.instanceColor) this.vaporTrailMesh.instanceColor.needsUpdate = true;
    }

    this.cooldownArcMesh.count = this.cooldownArcCount;
    if (this.cooldownArcCount > 0) {
      this.cooldownArcMesh.instanceMatrix.needsUpdate = true;
      if (this.cooldownArcMesh.instanceColor) this.cooldownArcMesh.instanceColor.needsUpdate = true;
      const cdAttr = this.cooldownArcMesh.geometry.getAttribute('aCooldownFraction');
      if (cdAttr) (cdAttr as THREE.BufferAttribute).needsUpdate = true;
    }

    this.updateCompressionRings(dt);
    this.updateFarPointBuffers();

    this.computeSwarmCentroid();
    this.updateDataLinks();
    this.updateIFFHeartbeat();
    this.updateECMParticles(dt);
    this.updateSelectionVisuals();
    this.droneTrails.update(deltaMs, this.motionStates);
  }

  getDroneGroup(): THREE.Group {
    return this.droneGroup;
  }

  dispose(): void {
    for (const mesh of this.instancedMeshes) {
      mesh.geometry.dispose();
      this.droneGroup.remove(mesh);
    }
    this.droneMaterial.dispose();
    this.instancedMeshes.length = 0;

    this.shadowMesh.geometry.dispose();
    (this.shadowMesh.material as THREE.Material).dispose();
    this.trailMesh.geometry.dispose();
    (this.trailMesh.material as THREE.Material).dispose();
    this.vaporTrailMesh.geometry.dispose();
    (this.vaporTrailMesh.material as THREE.Material).dispose();
    this.droneGroup.remove(this.shadowMesh);
    this.droneGroup.remove(this.trailMesh);
    this.droneGroup.remove(this.vaporTrailMesh);

    for (const ring of this.compressionRings) {
      ring.mesh.geometry.dispose();
      ring.material.dispose();
      this.droneGroup.remove(ring.mesh);
    }
    this.compressionRings.length = 0;

    this.farPoints.geometry.dispose();
    (this.farPoints.material as THREE.Material).dispose();
    this.droneGroup.remove(this.farPoints);

    this.dataLinkMesh.geometry.dispose();
    (this.dataLinkMesh.material as THREE.Material).dispose();
    this.droneGroup.remove(this.dataLinkMesh);

    this.ecmPoints.geometry.dispose();
    (this.ecmPoints.material as THREE.Material).dispose();
    this.droneGroup.remove(this.ecmPoints);

    this.selectionRings.forEach((ring) => this.droneGroup.remove(ring));
    this.selectionRings.clear();
    this.leaderMarkers.forEach((marker) => this.droneGroup.remove(marker));
    this.leaderMarkers.clear();
    this.selectionRingTemplate.dispose();
    this.selectionRingMaterial.dispose();
    this.leaderMarkerTemplate.dispose();
    this.leaderMarkerMaterial.dispose();

    this.cooldownArcMesh.geometry.dispose();
    (this.cooldownArcMesh.material as THREE.Material).dispose();
    this.droneGroup.remove(this.cooldownArcMesh);

    this.scene.remove(this.droneGroup);

    this.droneTrails.dispose();

    this.droneSprites.forEach((entry) => this.disposeSpriteEntry(entry));
    this.droneSprites.clear();
    this.healthBars.forEach((entry) => this.disposeHealthBar(entry));
    this.healthBars.clear();
    this.spriteTextures.forEach((texture) => texture.dispose());
    this.spriteTextures.clear();
    this.deathPendingTimers.clear();
  }

  private getSpriteTexture(droneType: number): THREE.Texture {
    const type = Math.min(Math.max(droneType, 0), DRONE_TYPE_COUNT - 1);
    let texture = this.spriteTextures.get(type);
    if (!texture) {
      const visual = getDroneSpriteVisual(type);
      texture = this.spriteLoader.load(visual.url);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = true;
      this.spriteTextures.set(type, texture);
    }
    return texture;
  }

  private createSpriteEntry(droneId: string, droneType: number): DroneSpriteEntry {
    const type = Math.min(Math.max(droneType, 0), DRONE_TYPE_COUNT - 1);
    const texture = this.getSpriteTexture(type);

    const material = new THREE.SpriteNodeMaterial({
      map: texture,
      color: 0xffffff,
      transparent: true,
      alphaTest: 0.03,
      depthWrite: false,
      depthTest: true,
      sizeAttenuation: true,
      toneMapped: false,
    });

    const glowMaterial = new THREE.SpriteNodeMaterial({
      map: texture,
      color: 0x88f4ff,
      transparent: true,
      opacity: 0.26,
      alphaTest: 0.02,
      depthWrite: false,
      depthTest: true,
      sizeAttenuation: true,
      toneMapped: false,
      blending: THREE.AdditiveBlending,
    });

    const glowSprite = new THREE.Sprite(glowMaterial as unknown as THREE.SpriteMaterial);
    glowSprite.frustumCulled = false;
    glowSprite.raycast = _noopRaycast;
    this.droneGroup.add(glowSprite);

    const sprite = new THREE.Sprite(material as unknown as THREE.SpriteMaterial);
    sprite.frustumCulled = false;
    this.droneGroup.add(sprite);

    const instanceIndex = this.allocateInstanceSlot(type, droneId);

    return { type, instanceIndex, sprite, material, glowSprite, glowMaterial };
  }

  private createHealthBarEntry(droneId: string): HealthBarEntry {
    const backgroundMaterial = new THREE.SpriteNodeMaterial({
      color: 0x101727,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      depthTest: true,
      sizeAttenuation: true,
      toneMapped: false,
    });
    const fillMaterial = new THREE.SpriteNodeMaterial({
      color: 0x12ff84,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      depthTest: true,
      sizeAttenuation: true,
      toneMapped: false,
    });

    const background = new THREE.Sprite(backgroundMaterial as unknown as THREE.SpriteMaterial);
    background.frustumCulled = false;
    background.raycast = _noopRaycast;
    background.userData.healthBar = true;
    background.userData.droneId = droneId;
    this.droneGroup.add(background);

    const fill = new THREE.Sprite(fillMaterial as unknown as THREE.SpriteMaterial);
    fill.frustumCulled = false;
    fill.raycast = _noopRaycast;
    fill.userData.healthBar = true;
    fill.userData.healthBarFill = true;
    fill.userData.droneId = droneId;
    this.droneGroup.add(fill);

    return { background, backgroundMaterial, fill, fillMaterial };
  }

  /** Reserve the first empty slot in the per-type `instanceDroneIds` table
   *  so removal can zero the matching InstancedMesh matrix slot. Falls back
   *  to slot 0 if the table is full (zeroing is still safe). */
  private allocateInstanceSlot(type: number, droneId: string): number {
    const slots = this.instanceDroneIds[type];
    for (let i = 0; i < slots.length; i++) {
      if (slots[i] === '') {
        slots[i] = droneId;
        return i;
      }
    }
    return 0;
  }

  private releaseInstanceSlot(type: number, instanceIndex: number): void {
    const slots = this.instanceDroneIds[type];
    if (instanceIndex >= 0 && instanceIndex < slots.length) {
      slots[instanceIndex] = '';
    }
  }

  private disposeSpriteEntry(entry: DroneSpriteEntry): void {
    // Hide before removing from scene — guards against a 1-frame ghost in
    // case a renderer pass references the sprite mid-removal.
    entry.sprite.visible = false;
    entry.glowSprite.visible = false;
    entry.sprite.userData.droneId = undefined;
    this.droneGroup.remove(entry.sprite);
    this.droneGroup.remove(entry.glowSprite);
    entry.material.dispose();
    entry.glowMaterial.dispose();

    // Zero the freed slot's matrix so a slot reuse can't flash this drone's
    // last write before the new occupant's first frame lands. Visually a
    // no-op while `mesh.count === 0`, but kept correct for future paths
    // that may grow `count`.
    const mesh = this.instancedMeshes[entry.type];
    if (mesh) {
      mesh.setMatrixAt(entry.instanceIndex, ZERO_MATRIX);
      mesh.instanceMatrix.needsUpdate = true;
    }
    this.releaseInstanceSlot(entry.type, entry.instanceIndex);
  }

  private disposeHealthBar(entry: HealthBarEntry): void {
    entry.background.visible = false;
    entry.fill.visible = false;
    this.droneGroup.remove(entry.background);
    this.droneGroup.remove(entry.fill);
    entry.backgroundMaterial.dispose();
    entry.fillMaterial.dispose();
  }

  private initInstancedMeshes(): void {
    // MeshStandardNodeMaterial replaces the legacy MeshStandardMaterial +
    // onBeforeCompile string surgery. The engine core / spawn scan / invuln /
    // rim / sun glint / ambient lift are all composed in TSL via
    // `buildDroneEmissive()` and folded into the standard PBR pipeline by
    // `emissiveNode`. Vertex displacement for the spawn sweep is set via
    // `positionNode`. `customProgramCacheKey` is no longer needed —
    // NodeMaterial caches by node graph structure.
    this.droneMaterial = new THREE.MeshStandardNodeMaterial({
      color: 0xffffff,
      vertexColors: true,
      flatShading: true,
      metalness: 0.22,
      roughness: 0.32,
      envMapIntensity: 2.2,
      alphaTest: 0.01,
      transparent: true,
    });

    // positionNode drives the spawn scan vertex push. Returning a displaced
    // `positionLocal` node lets the node material handle the
    // instanceMatrix/modelMatrix transforms automatically.
    this.droneMaterial.positionNode = buildDroneVertexDisplacement();

    // colorNode — dark tactical hull. Per-drone tactical color still feeds
    // emissive/rim/trails through InstancedMesh.setColorAt().
    this.droneMaterial.colorNode = buildDroneColor(vec3(0.035, 0.045, 0.055));

    // emissiveNode — composes the additive PBR emissive path (engine glow,
    // rim, sun glint, armor, ambient lift). Writes into the MRT `emissive`
    // target for selective bloom.
    this.droneMaterial.emissiveNode = buildDroneEmissive();

    // opacityNode — spawn-progress alpha ramp. Returns 1.0 after materialize.
    this.droneMaterial.opacityNode = buildDroneOpacity();

    for (let type = 0; type < DRONE_TYPE_COUNT; type++) {
      const geometry = this.buildMergedGeometry(type);
      const mesh = new THREE.InstancedMesh(geometry, this.droneMaterial, MAX_INSTANCES);
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.userData.instanceDroneIds = this.instanceDroneIds[type];

      const spBuf = new Float32Array(MAX_INSTANCES);
      const invBuf = new Float32Array(MAX_INSTANCES);
      const locBuf = new Float32Array(MAX_INSTANCES);
      this.attrSpawnProgress.push(spBuf);
      this.attrInvulnerable.push(invBuf);
      this.attrIsLocal.push(locBuf);

      geometry.setAttribute(
        'aSpawnProgress',
        new THREE.InstancedBufferAttribute(spBuf, 1),
      );
      geometry.setAttribute(
        'aInvulnerable',
        new THREE.InstancedBufferAttribute(invBuf, 1),
      );
      geometry.setAttribute(
        'aIsLocal',
        new THREE.InstancedBufferAttribute(locBuf, 1),
      );

      this.instancedMeshes.push(mesh);
      this.droneGroup.add(mesh);
    }
  }

  private initCloseEffects(): void {
    // Shadow disc — MeshBasicNodeMaterial with a radial dark `colorNode`
    // driven by the per-instance strength written via `setColorAt`.
    const shadowGeo = new THREE.CircleGeometry(1, 18);
    const shadowMat = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });
    shadowMat.colorNode = buildShadowColor();
    this.shadowMesh = new THREE.InstancedMesh(shadowGeo, shadowMat, MAX_INSTANCES);
    this.shadowMesh.count = 0;
    this.shadowMesh.frustumCulled = false;
    this.shadowMesh.raycast = _noopRaycast;
    this.droneGroup.add(this.shadowMesh);

    // Engine trail — the custom scanline + hash noise trail pulse from the
    // legacy GLSL is reproduced via `buildTrailColor()`. Using emissiveNode
    // (in addition to colorNode) writes non-zero emissive so the MRT bloom
    // pipeline still lights up trails.
    const trailGeo = new THREE.ConeGeometry(0.45, 1, 6, 1, true);
    trailGeo.rotateX(Math.PI / 2);
    trailGeo.translate(0, 0, -0.5);
    const trailMat = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    trailMat.colorNode = buildTrailColor();
    this.trailMesh = new THREE.InstancedMesh(trailGeo, trailMat, MAX_INSTANCES);
    this.trailMesh.count = 0;
    this.trailMesh.frustumCulled = false;
    this.trailMesh.raycast = _noopRaycast;
    this.droneGroup.add(this.trailMesh);

    // Vapor condensation contrail — soft white, radial falloff.
    const vaporGeo = new THREE.ConeGeometry(0.15, 1, 4, 1, true);
    vaporGeo.rotateX(Math.PI / 2);
    vaporGeo.translate(0, 0, -0.5);
    const vaporMat = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    vaporMat.colorNode = buildVaporTrailColor();
    this.vaporTrailMesh = new THREE.InstancedMesh(vaporGeo, vaporMat, MAX_VAPOR_INSTANCES);
    this.vaporTrailMesh.count = 0;
    this.vaporTrailMesh.frustumCulled = false;
    this.vaporTrailMesh.raycast = _noopRaycast;
    this.droneGroup.add(this.vaporTrailMesh);

    this.initCompressionRingPool();
  }

  private initFarPoints(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    this.farPointPositions = new Float32Array(MAX_INSTANCES * 3);
    this.farPointColors = new Float32Array(MAX_INSTANCES * 3);

    geometry.setAttribute('position', new THREE.BufferAttribute(this.farPointPositions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.farPointColors, 3));

    const FAR_POINT_SIZE_SCALE = 450.0;
    const material = new THREE.PointsNodeMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
    });
    material.sizeAttenuation = false;
    material.sizeNode = positionView.z.abs().reciprocal().mul(FAR_POINT_SIZE_SCALE);
    material.opacityNode = float(0.86);

    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.raycast = _noopRaycast;
    this.droneGroup.add(points);
    return points;
  }

  private updateMotionState(drone: InterpolatedDrone, dt: number): DroneMotionState {
    const wasDead = this.lastAliveSeen.get(drone.id) === false;
    this.lastAliveSeen.set(drone.id, true);

    let state = this.motionStates.get(drone.id);
    if (!state) {
      state = {
        position: new THREE.Vector3(drone.position.x, drone.position.y, drone.position.z),
        velocity: new THREE.Vector3(),
        displayRoll: 0,
        displayPitch: 0,
        lastHeading: drone.heading,
        speed: 0,
      };
      this.motionStates.set(drone.id, state);

      if (wasDead) {
        // Respawn — always replay the materialize sweep.
        this.spawnStates.set(drone.id, { spawnProgress: 0, age: 0 });
      } else if (!this.spawnStates.has(drone.id)) {
        // Drones seen for the first time with full health are pre-existing (e.g. joining mid-match);
        // skip the materialization effect by starting fully materialized.
        const isPreExisting = drone.health >= drone.maxHealth;
        this.spawnStates.set(drone.id, isPreExisting
          ? { spawnProgress: 1, age: SPAWN_INVULN_SEC + 1 }
          : { spawnProgress: 0, age: 0 });
      }
      return state;
    }

    const newVx = (drone.position.x - state.position.x) / dt;
    const newVy = (drone.position.y - state.position.y) / dt;
    const newVz = (drone.position.z - state.position.z) / dt;
    state.velocity.lerp(this._scratchVec3.set(newVx, newVy, newVz), 0.32);
    state.speed = state.velocity.length();

    const headingDelta = Math.atan2(
      Math.sin(drone.heading - state.lastHeading),
      Math.cos(drone.heading - state.lastHeading),
    );
    const rollTarget = THREE.MathUtils.clamp(-headingDelta * 5.0, -0.7, 0.7);
    const pitchTarget = THREE.MathUtils.clamp(state.speed * 0.012 + state.velocity.y * 0.02, -0.22, 0.28);

    state.displayRoll = THREE.MathUtils.lerp(state.displayRoll, rollTarget, 0.22);
    state.displayPitch = THREE.MathUtils.lerp(state.displayPitch, pitchTarget, 0.18);
    state.position.set(drone.position.x, drone.position.y, drone.position.z);
    state.lastHeading = drone.heading;

    return state;
  }

  private addSprite(
    drone: InterpolatedDrone,
    motion: DroneMotionState,
    isOwn: boolean,
    healthFrac: number,
  ): void {
    const type = Math.min(Math.max(drone.droneType, 0), DRONE_TYPE_COUNT - 1);
    let entry = this.droneSprites.get(drone.id);
    if (entry && entry.type !== type) {
      this.disposeSpriteEntry(entry);
      this.droneSprites.delete(drone.id);
      entry = undefined;
    }
    if (!entry) {
      entry = this.createSpriteEntry(drone.id, type);
      this.droneSprites.set(drone.id, entry);
    }

    const visual: DroneSpriteVisual = getDroneSpriteVisual(type);
    const ss = this.spawnStates.get(drone.id);
    const spawnProgress = ss ? ss.spawnProgress : 1;
    const age = ss ? ss.age : SPAWN_INVULN_SEC + 1;
    const invulnerable = age < SPAWN_INVULN_SEC
      ? Math.max(0, 1 - age / SPAWN_INVULN_SEC)
      : 0;
    const scalePulse = 1
      + Math.sin(this.time * 18.0 + drone.heading) * invulnerable * 0.08
      + Math.min(motion.speed * 0.004, 0.08);
    const spawnScale = THREE.MathUtils.lerp(0.35, 1.0, spawnProgress);
    const width = visual.width * scalePulse * spawnScale;
    const height = visual.height * scalePulse * spawnScale;
    const yOffset = visual.yOffset ?? 0;

    entry.sprite.position.set(drone.position.x, drone.position.y + yOffset, drone.position.z);
    entry.sprite.scale.set(width, height, 1);
    entry.sprite.visible = true;
    entry.sprite.userData.droneId = drone.id;

    this.computeDroneColor(isOwn, healthFrac, drone.tier);
    entry.material.color.copy(_whiteColor).lerp(this._color, visual.tintStrength ?? 0.2);
    const hitFlash = this.getHitFlash(drone.id);
    if (hitFlash > 0) {
      entry.material.color.lerp(_scratchRed, hitFlash);
    }
    entry.material.opacity = THREE.MathUtils.lerp(0.12, 1.0, spawnProgress);

    entry.glowSprite.position.copy(entry.sprite.position);
    entry.glowSprite.scale.set(
      width * (1.1 + invulnerable * 0.22),
      height * (1.1 + invulnerable * 0.22),
      1,
    );
    entry.glowSprite.visible = true;
    entry.glowMaterial.color.copy(this._color);
    if (hitFlash > 0) {
      entry.glowMaterial.color.lerp(_scratchRed, hitFlash);
    }
    entry.glowMaterial.opacity = spawnProgress * (0.16 + invulnerable * 0.24 + (isOwn ? 0.06 : 0));
    if (hitFlash > 0) {
      entry.glowMaterial.opacity = Math.min(0.9, entry.glowMaterial.opacity + hitFlash * 0.55);
    }
    // Hover rim-glow boost — only applied to the local player's drones, since
    // those are the only selectable units. ~40% glow opacity boost on top of
    // the base, capped at 1.0 to avoid clipping.
    if (isOwn && drone.id === this.hoveredDroneId) {
      entry.glowMaterial.opacity = Math.min(1.0, entry.glowMaterial.opacity * 1.4 + 0.12);
    }
    const critGlow = this.getCritGlow(drone.id);
    if (critGlow > 0) {
      entry.material.color.lerp(_whiteColor, critGlow * 0.45);
      entry.glowMaterial.color.lerp(_whiteColor, critGlow * 0.65);
      entry.glowMaterial.opacity = Math.min(1.0, entry.glowMaterial.opacity + critGlow * 0.5);
    }

    const screenRotation = this.computeSpriteScreenRotation(drone, motion) + motion.displayRoll * 0.25;
    entry.material.rotation = screenRotation;
    entry.glowMaterial.rotation = screenRotation;

    if (isOwn) {
      this.localDroneSpriteIds.push(drone.id);
    }
  }

  /**
   * Keep a freshly-killed drone's sprite rendered for {@link DEATH_FX_DURATION_SEC},
   * fading proportionally so the EffectsManager explosion can overlay the body.
   * Position is held by the sprite's last transform (set in the prior addSprite call).
   */
  private handleDeathPending(drone: InterpolatedDrone): void {
    const entry = this.droneSprites.get(drone.id);
    if (!entry) return;
    const remaining = this.deathPendingTimers.get(drone.id) ?? 0;
    const fade = THREE.MathUtils.clamp(remaining / DEATH_FX_DURATION_SEC, 0, 1);
    entry.sprite.visible = true;
    entry.glowSprite.visible = true;
    entry.material.opacity = fade;
    entry.glowMaterial.opacity = fade * 0.4;
  }

  private updateHealthBar(drone: InterpolatedDrone, isOwn: boolean, healthFrac: number): void {
    if (isOwn || !drone.alive) return;

    let entry = this.healthBars.get(drone.id);
    if (!entry) {
      entry = this.createHealthBarEntry(drone.id);
      this.healthBars.set(drone.id, entry);
    }

    const clamped = THREE.MathUtils.clamp(healthFrac, 0, 1);
    const barWidth = 5.8;
    const barHeight = 0.38;
    const y = drone.position.y + 5.1;

    entry.background.position.set(drone.position.x, y, drone.position.z);
    entry.background.scale.set(barWidth, barHeight, 1);
    entry.background.visible = true;

    entry.fill.position.set(drone.position.x, y + 0.01, drone.position.z);
    entry.fill.scale.set(Math.max(0.05, barWidth * clamped), barHeight * 0.58, 1);
    entry.fill.visible = true;

    if (clamped < 0.35) {
      entry.fillMaterial.color.copy(_scratchRed);
    } else if (clamped < 0.65) {
      entry.fillMaterial.color.copy(_barAmber);
    } else {
      entry.fillMaterial.color.copy(_barGreen);
    }
  }

  private getHitFlash(droneId: string): number {
    const remaining = this.hitFlashTimers.get(droneId) ?? 0;
    if (remaining <= 0) return 0;
    return THREE.MathUtils.clamp(remaining / 0.28, 0, 1);
  }

  private getCritGlow(droneId: string): number {
    const remaining = this.critGlowTimers.get(droneId) ?? 0;
    if (remaining <= 0) return 0;
    return THREE.MathUtils.clamp(remaining / 0.2, 0, 1);
  }

  private computeSpriteScreenRotation(drone: InterpolatedDrone, motion: DroneMotionState): number {
    if (motion.speed < 0.05) return 0;

    const viewDir = this._scratchVec3
      .set(
        this.cameraPos.x - drone.position.x,
        this.cameraPos.y - drone.position.y,
        this.cameraPos.z - drone.position.z,
      )
      .normalize();
    const worldUp = this._trailOffset.set(0, 1, 0);
    const screenRight = this._wingtipOffset.crossVectors(worldUp, viewDir);
    if (screenRight.lengthSq() < 0.0001) {
      screenRight.set(1, 0, 0);
    } else {
      screenRight.normalize();
    }
    const screenUp = this._ringDir.crossVectors(viewDir, screenRight).normalize();

    const headingDir = this._shadowPos.set(
      Math.sin(drone.heading),
      0,
      Math.cos(drone.heading),
    );
    const screenX = headingDir.dot(screenRight);
    const screenY = headingDir.dot(screenUp);
    if (Math.abs(screenX) + Math.abs(screenY) < 0.0001) return 0;
    return Math.atan2(screenX, screenY);
  }

  private addCloseEffects(
    drone: InterpolatedDrone,
    motion: DroneMotionState,
    isOwn: boolean,
    healthFrac: number,
  ): void {
    this._euler.set(
      drone.pitch + motion.displayPitch,
      drone.heading,
      motion.displayRoll,
      'YXZ',
    );
    this._quaternion.setFromEuler(this._euler);

    if (this.qualityPreset.features.blobShadows) {
      const shadowIdx = this.closeFxCount;
      if (shadowIdx < MAX_INSTANCES) {
        this.closeFxCount = shadowIdx + 1;
        const terrainY = sampleTerrainWorldHeight(drone.position.x, drone.position.z, this.worldTheme) + 0.04;
        const altitude = Math.max(0, drone.position.y - terrainY);
        const shadowSize = THREE.MathUtils.clamp(2.3 - altitude * 0.03, 0.8, 2.4);
        const shadowStrength = THREE.MathUtils.clamp(1 - altitude / 28, 0.08, 0.75);
        this._shadowPos.set(drone.position.x, terrainY, drone.position.z);
        this._shadowScale.set(shadowSize, shadowSize, 1);
        this._shadowMatrix.compose(this._shadowPos, this._shadowQuaternion, this._shadowScale);
        this.shadowMesh.setMatrixAt(shadowIdx, this._shadowMatrix);
        this.shadowMesh.setColorAt(
          shadowIdx,
          this._scratchColor3.setRGB(shadowStrength, shadowStrength, shadowStrength),
        );
      }
    }

    if (!this.qualityPreset.features.engineTrails || this.trailCount >= this.qualityPreset.trailBudget) {
      return;
    }

    const idx = this.trailCount;
    this.trailCount += 1;
    this.computeDroneColor(isOwn, healthFrac, drone.tier);
    const trailLength = THREE.MathUtils.clamp(1.6 + motion.speed * 0.05, 1.5, 3.8);
    const trailWidth = THREE.MathUtils.clamp(0.42 + motion.speed * 0.01, 0.4, 0.85);
    this._trailOffset.set(0, 0, trailLength * 0.55).applyQuaternion(this._quaternion);
    this._shadowPos.set(
      drone.position.x - this._trailOffset.x,
      drone.position.y - this._trailOffset.y,
      drone.position.z - this._trailOffset.z,
    );
    this._shadowScale.set(trailWidth, trailWidth * 0.82, trailLength);
    this._shadowMatrix.compose(this._shadowPos, this._quaternion, this._shadowScale);
    this.trailMesh.setMatrixAt(idx, this._shadowMatrix);
    this.trailMesh.setColorAt(idx, _scratchColor2.copy(this._color).multiplyScalar(1.18));

    const absRoll = Math.abs(motion.displayRoll);
    if (isOwn && absRoll > VAPOR_ROLL_THRESHOLD) {
      const vaporLen = THREE.MathUtils.clamp(1.0 + absRoll * 2.0, 1.0, 3.0);
      const rollAlpha = THREE.MathUtils.clamp(absRoll * 1.5, 0, 1);
      const vaporWidth = 0.15 + absRoll * 0.1;

      for (const side of [-2.0, 2.0]) {
        if (this.vaporTrailCount >= MAX_VAPOR_INSTANCES) break;
        const vi = this.vaporTrailCount++;

        this._wingtipOffset.set(side, 0, vaporLen * 0.55);
        this._wingtipOffset.applyQuaternion(this._quaternion);
        this._shadowPos.set(
          drone.position.x - this._wingtipOffset.x,
          drone.position.y - this._wingtipOffset.y,
          drone.position.z - this._wingtipOffset.z,
        );
        this._shadowScale.set(vaporWidth, vaporWidth * 0.7, vaporLen);
        this._shadowMatrix.compose(this._shadowPos, this._quaternion, this._shadowScale);
        this.vaporTrailMesh.setMatrixAt(vi, this._shadowMatrix);
        this._color.setRGB(rollAlpha, rollAlpha, rollAlpha);
        this.vaporTrailMesh.setColorAt(vi, this._color);
      }
    }

    if (isOwn && motion.speed > SONIC_SPEED_THRESHOLD) {
      const lastSpawn = this.ringCooldowns.get(drone.id) ?? 0;
      if (this.time - lastSpawn >= COMPRESSION_RING_COOLDOWN) {
        this._wingtipOffset.set(drone.position.x, drone.position.y, drone.position.z);
        this.spawnCompressionRing(this._wingtipOffset, motion.velocity);
        this.ringCooldowns.set(drone.id, this.time);
      }
    }
  }

  private addFarPoint(drone: InterpolatedDrone, isOwn: boolean, healthFrac: number): void {
    const idx = this.farPointCount;
    if (idx >= MAX_INSTANCES) return;
    this.farPointCount = idx + 1;

    const i3 = idx * 3;
    this.farPointPositions[i3] = drone.position.x;
    this.farPointPositions[i3 + 1] = drone.position.y;
    this.farPointPositions[i3 + 2] = drone.position.z;

    this.computeDroneColor(isOwn, healthFrac, drone.tier);
    this.farPointColors[i3] = this._color.r;
    this.farPointColors[i3 + 1] = this._color.g;
    this.farPointColors[i3 + 2] = this._color.b;
  }

  private updateFarPointBuffers(): void {
    const geo = this.farPoints.geometry;
    geo.setDrawRange(0, this.farPointCount);

    if (this.farPointCount > 0) {
      geo.attributes.position.needsUpdate = true;
      geo.attributes.color.needsUpdate = true;
    }
  }

  private computeDroneColor(isOwn: boolean, healthFrac: number, tier: number): void {
    _scratchBase.copy(isOwn ? _ownColor : _enemyColor);
    _scratchBase.lerp(_scratchRed, Math.max(0, 1 - healthFrac) * 0.58);
    const intensity = (TIER_EMISSIVE_INTENSITY[tier] ?? 0.45) * (0.78 + healthFrac * 0.42);
    this._color.copy(_scratchBase).multiplyScalar(1.18 + intensity);
  }

  /* ────────────────────────────────────────────────────────
   * C2 Data Links — animated data packets along line segments
   * ──────────────────────────────────────────────────────── */

  private initDataLinks(): void {
    const maxLinks = MAX_DRONES_PER_PLAYER * (MAX_DRONES_PER_PLAYER - 1) / 2;
    const maxVerts = maxLinks * 2;

    this.dataLinkPositions = new Float32Array(maxVerts * 3);
    this.dataLinkColors = new Float32Array(maxVerts * 3);
    this.dataLinkDistances = new Float32Array(maxVerts);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.dataLinkPositions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.dataLinkColors, 3));
    geometry.setAttribute('lineDistance', new THREE.BufferAttribute(this.dataLinkDistances, 1));

    const material = new THREE.LineBasicNodeMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    material.colorNode = buildDataLinkColor();

    this.dataLinkMesh = new THREE.LineSegments(geometry, material);
    this.dataLinkMesh.frustumCulled = false;
    this.dataLinkMesh.raycast = _noopRaycast;
    this.droneGroup.add(this.dataLinkMesh);
  }

  private updateDataLinks(): void {
    const pos = this.localDronePositions;
    const len = this.localDroneCount;
    this.dataLinkCount = 0;

    if (len < 2) {
      this.dataLinkMesh.geometry.setDrawRange(0, 0);
      return;
    }

    const maxDist = 30;
    const maxDistSq = maxDist * maxDist;
    let vi = 0;

    for (let i = 0; i < len; i++) {
      const ai = i * 3;
      const ax = pos[ai], ay = pos[ai + 1], az = pos[ai + 2];
      for (let j = i + 1; j < len; j++) {
        const bi = j * 3;
        const dx = pos[bi] - ax, dy = pos[bi + 1] - ay, dz = pos[bi + 2] - az;
        const distSq = dx * dx + dy * dy + dz * dz;
        if (distSq > maxDistSq) continue;

        const dist = Math.sqrt(distSq);
        const alpha = 1.0 - dist / maxDist;
        const v6 = vi * 6;
        const v2 = vi * 2;

        this.dataLinkPositions[v6] = ax;
        this.dataLinkPositions[v6 + 1] = ay;
        this.dataLinkPositions[v6 + 2] = az;
        this.dataLinkPositions[v6 + 3] = pos[bi];
        this.dataLinkPositions[v6 + 4] = pos[bi + 1];
        this.dataLinkPositions[v6 + 5] = pos[bi + 2];

        this.dataLinkColors[v6] = alpha;
        this.dataLinkColors[v6 + 1] = alpha;
        this.dataLinkColors[v6 + 2] = alpha;
        this.dataLinkColors[v6 + 3] = alpha;
        this.dataLinkColors[v6 + 4] = alpha;
        this.dataLinkColors[v6 + 5] = alpha;

        this.dataLinkDistances[v2] = 0;
        this.dataLinkDistances[v2 + 1] = dist;

        vi++;
        this.dataLinkCount++;
      }
    }

    const geo = this.dataLinkMesh.geometry;
    geo.setDrawRange(0, this.dataLinkCount * 2);
    if (this.dataLinkCount > 0) {
      geo.attributes.position.needsUpdate = true;
      geo.attributes.color.needsUpdate = true;
      geo.attributes.lineDistance.needsUpdate = true;
    }
  }

  /* ────────────────────────────────────────────────────────
   * IFF Heartbeat Pulse — brightness wave from swarm centroid
   * ──────────────────────────────────────────────────────── */

  private computeSwarmCentroid(): void {
    const pos = this.localDronePositions;
    const len = this.localDroneCount;
    this.swarmCentroid.set(0, 0, 0);
    this.maxSwarmRadius = 0;

    if (len === 0) return;

    for (let i = 0; i < len; i++) {
      const off = i * 3;
      this.swarmCentroid.x += pos[off];
      this.swarmCentroid.y += pos[off + 1];
      this.swarmCentroid.z += pos[off + 2];
    }
    this.swarmCentroid.divideScalar(len);

    const cx = this.swarmCentroid.x, cy = this.swarmCentroid.y, cz = this.swarmCentroid.z;
    for (let i = 0; i < len; i++) {
      const off = i * 3;
      const dx = pos[off] - cx, dy = pos[off + 1] - cy, dz = pos[off + 2] - cz;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d > this.maxSwarmRadius) this.maxSwarmRadius = d;
    }
  }

  private updateIFFHeartbeat(): void {
    if (this.heartbeatTimer >= this.HEARTBEAT_DURATION) return;
    if (this.localDroneSpriteIds.length === 0) return;

    const pulseProgress = this.heartbeatTimer / this.HEARTBEAT_DURATION;
    const waveFront = pulseProgress * (this.maxSwarmRadius + 5.0);
    const waveWidth = 4.0;

    for (const id of this.localDroneSpriteIds) {
      const entry = this.droneSprites.get(id);
      if (!entry) continue;
      const distFromCenter = entry.sprite.position.distanceTo(this.swarmCentroid);
      const waveDist = Math.abs(distFromCenter - waveFront);

      if (waveDist < waveWidth) {
        const boost = 1.0 - waveDist / waveWidth;
        entry.material.color.multiplyScalar(1.0 + 0.1 * boost);
        entry.glowMaterial.opacity = Math.min(0.72, entry.glowMaterial.opacity + 0.16 * boost);
      }
    }
  }

  /* ────────────────────────────────────────────────────────
   * ECM Interference Particles — flickering orbit around swarm
   * ──────────────────────────────────────────────────────── */

  private initECM(): void {
    const n = this.ECM_PARTICLE_COUNT;
    this.ecmPositions = new Float32Array(n * 3);
    this.ecmAlphas = new Float32Array(n);
    this.ecmSizes = new Float32Array(n);

    for (let i = 0; i < n; i++) {
      this.ecmAlphas[i] = 0;
      this.ecmSizes[i] = 1.5 + Math.random() * 2.0;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.ecmPositions, 3));
    geometry.setAttribute('alpha', new THREE.BufferAttribute(this.ecmAlphas, 1));
    geometry.setAttribute('size', new THREE.BufferAttribute(this.ecmSizes, 1));

    // PointsNodeMaterial with manual perspective sizing via sizeNode.
    // sizeAttenuation must be false because buildEcmSize() reconstructs
    // the perspective formula manually (matching EnvironmentParticles).
    const material = new THREE.PointsNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: false,
    });
    material.sizeNode = buildEcmSize();
    material.colorNode = buildEcmColor();

    this.ecmPoints = new THREE.Points(geometry, material);
    this.ecmPoints.frustumCulled = false;
    this.ecmPoints.raycast = _noopRaycast;
    this.droneGroup.add(this.ecmPoints);
  }

  private updateECMParticles(dt: number): void {
    const n = this.ECM_PARTICLE_COUNT;
    const localCount = this.localDroneCount;

    if (localCount < 2) {
      for (let i = 0; i < n; i++) this.ecmAlphas[i] = 0;
      const geo = this.ecmPoints.geometry;
      geo.attributes.alpha.needsUpdate = true;
      return;
    }

    const cx = this.swarmCentroid.x;
    const cy = this.swarmCentroid.y;
    const cz = this.swarmCentroid.z;
    const orbitRadius = Math.min(this.maxSwarmRadius * 0.7, 12);

    for (let i = 0; i < n; i++) {
      const phase = this.ecmPhases[i];
      const freq = 0.4 + (i % 5) * 0.08;
      const freq2 = 0.3 + (i % 3) * 0.1;
      const vertOffset = 2.0 + (i % 4) * 0.8;

      const i3 = i * 3;
      this.ecmPositions[i3] = cx + orbitRadius * Math.sin(this.time * freq + phase);
      this.ecmPositions[i3 + 1] = cy + vertOffset * Math.cos(this.time * freq2 + phase * 1.3);
      this.ecmPositions[i3 + 2] = cz + orbitRadius * Math.cos(this.time * freq + phase);

      this.ecmFlickerTimers[i] -= dt;
      if (this.ecmFlickerTimers[i] <= 0) {
        this.ecmFlickerStates[i] = this.ecmFlickerStates[i] ? 0 : 1;
        this.ecmFlickerIntervals[i] = 0.05 + Math.random() * 0.25;
        this.ecmFlickerTimers[i] = this.ecmFlickerIntervals[i];
        this.ecmAlphas[i] = this.ecmFlickerStates[i] ? (0.3 + Math.random() * 0.3) : 0.0;
      }
    }

    const geo = this.ecmPoints.geometry;
    geo.attributes.position.needsUpdate = true;
    geo.attributes.alpha.needsUpdate = true;
  }

  /* ── Compression ring pool ──────────────────────────────────── */

  private initCompressionRingPool(): void {
    const ringGeo = new THREE.TorusGeometry(1, 0.08, 8, 24);

    for (let i = 0; i < COMPRESSION_RING_POOL_SIZE; i++) {
      const mat = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const progressUniform = uniform(0);
      mat.colorNode = buildCompressionRingColor(progressUniform);
      const mesh = new THREE.Mesh(ringGeo, mat);
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.raycast = _noopRaycast;
      this.droneGroup.add(mesh);
      this.compressionRings.push({ mesh, material: mat, progressUniform, active: false, progress: 0 });
    }
  }

  private spawnCompressionRing(position: THREE.Vector3, velocity: THREE.Vector3): void {
    const ring = this.compressionRings.find(r => !r.active);
    if (!ring) return;

    ring.active = true;
    ring.progress = 0;
    ring.mesh.visible = true;
    ring.mesh.position.copy(position);

    this._ringDir.copy(velocity);
    if (this._ringDir.lengthSq() > 0.01) {
      this._ringDir.normalize();
      this._ringQuat.setFromUnitVectors(_forward, this._ringDir);
      ring.mesh.quaternion.copy(this._ringQuat);
    }

    ring.mesh.scale.setScalar(0.5);
    ring.progressUniform.value = 0;
  }

  private updateCompressionRings(dt: number): void {
    for (const ring of this.compressionRings) {
      if (!ring.active) continue;
      ring.progress += dt / COMPRESSION_RING_DURATION;
      if (ring.progress >= 1.0) {
        ring.active = false;
        ring.mesh.visible = false;
        continue;
      }
      const scale = 0.5 + ring.progress * 3.5;
      ring.mesh.scale.set(scale, scale, scale);
      ring.progressUniform.value = ring.progress;
    }
  }

  private buildMergedGeometry(droneType: number): THREE.BufferGeometry {
    switch (droneType) {
      case 0: return this.mergeStrikerGeometry();
      case 1: return this.mergeSniperGeometry();
      case 2: return this.mergeHealerGeometry();
      case 3: return this.mergeInterceptorGeometry();
      case 4: return this.mergeSupportGeometry();
      case 5: return this.mergeGuardianGeometry();
      default: return this.mergeStrikerGeometry();
    }
  }

  private mergeStrikerGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];

    const coreGeo = new THREE.OctahedronGeometry(0.42, 0);
    coreGeo.scale(1.05, 0.42, 1.35);
    coreGeo.rotateY(Math.PI / 4);
    parts.push(coreGeo);

    const noseGeo = new THREE.ConeGeometry(0.28, 0.9, 4);
    noseGeo.rotateX(Math.PI / 2);
    const nose = noseGeo.clone();
    nose.translate(0, 0, -0.86);
    parts.push(nose);

    const wingGeo = new THREE.BoxGeometry(0.85, 0.08, 0.28);
    for (const side of [-1, 1]) {
      const wing = wingGeo.clone();
      wing.rotateY(side * 0.52);
      wing.translate(side * 0.56, 0.02, 0.02);
      parts.push(wing);
    }

    const cannonGeo = new THREE.CylinderGeometry(0.045, 0.06, 0.82, 8);
    cannonGeo.rotateX(Math.PI / 2);

    for (const side of [-1, 1]) {
      const cannon = cannonGeo.clone();
      cannon.translate(side * 0.26, 0.1, -0.56);
      parts.push(cannon);
    }

    const engineGeo = new THREE.CylinderGeometry(0.14, 0.18, 0.22, 10);
    engineGeo.rotateX(Math.PI / 2);
    engineGeo.translate(0, 0, 0.72);
    parts.push(engineGeo);

    return this.mergeAndDispose(parts);
  }

  private mergeSniperGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];

    const bodyGeo = new THREE.CylinderGeometry(0.18, 0.28, 1.45, 6);
    bodyGeo.rotateX(Math.PI / 2);
    bodyGeo.translate(0, 0, -0.1);
    parts.push(bodyGeo);

    const barrelGeo = new THREE.CylinderGeometry(0.04, 0.06, 1.15, 8);
    barrelGeo.rotateX(Math.PI / 2);
    barrelGeo.translate(0, 0.03, -1.12);
    parts.push(barrelGeo);

    const railGeo = new THREE.BoxGeometry(0.16, 0.08, 0.95);
    railGeo.translate(0, 0.22, -0.52);
    parts.push(railGeo);

    const opticGeo = new THREE.SphereGeometry(0.13, 10, 8);
    opticGeo.scale(1.0, 0.7, 1.2);
    opticGeo.translate(0, 0.32, -0.78);
    parts.push(opticGeo);

    const finGeo = new THREE.BoxGeometry(0.36, 0.06, 0.34);
    for (const side of [-1, 1]) {
      const fin = finGeo.clone();
      fin.rotateY(side * -0.55);
      fin.translate(side * 0.26, 0, 0.46);
      parts.push(fin);
    }

    return this.mergeAndDispose(parts);
  }

  private mergeHealerGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];

    const core = new THREE.SphereGeometry(0.34, 16, 10);
    core.scale(1.0, 0.86, 1.0);
    parts.push(core);

    const ringGeo = new THREE.TorusGeometry(0.65, 0.04, 8, 16);
    ringGeo.rotateX(Math.PI / 2);
    parts.push(ringGeo);

    const tiltedRing = new THREE.TorusGeometry(0.52, 0.028, 8, 16);
    tiltedRing.rotateX(Math.PI / 2);
    tiltedRing.rotateY(Math.PI / 4);
    parts.push(tiltedRing);

    const crossBar = new THREE.BoxGeometry(0.9, 0.08, 0.16);
    parts.push(crossBar);
    const crossBar2 = new THREE.BoxGeometry(0.16, 0.08, 0.9);
    parts.push(crossBar2);

    const podGeo = new THREE.SphereGeometry(0.11, 8, 6);
    for (const [x, z] of [[0.52, 0], [-0.52, 0], [0, 0.52], [0, -0.52]]) {
      const pod = podGeo.clone();
      pod.translate(x, 0, z);
      parts.push(pod);
    }

    return this.mergeAndDispose(parts);
  }

  private mergeInterceptorGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];

    const hullGeo = new THREE.SphereGeometry(0.35, 16, 12);
    hullGeo.scale(0.72, 0.28, 1.65);
    hullGeo.translate(0, 0, -0.05);
    parts.push(hullGeo);

    const wingGeo = new THREE.BoxGeometry(0.78, 0.055, 0.24);
    for (const side of [-1, 1]) {
      const wing = wingGeo.clone();
      wing.rotateY(side * 0.72);
      wing.translate(side * 0.48, 0.01, -0.08);
      parts.push(wing);
    }

    const rotorGeo = new THREE.TorusGeometry(0.16, 0.035, 8, 14);
    rotorGeo.rotateX(Math.PI / 2);
    for (const [x, z] of [[0.46, 0.42], [-0.46, 0.42], [0.34, -0.62], [-0.34, -0.62]]) {
      const rotor = rotorGeo.clone();
      rotor.translate(x, 0, z);
      parts.push(rotor);
    }

    const sensorGeo = new THREE.SphereGeometry(0.055, 8, 6);
    for (const side of [-1, 1]) {
      const sensor = sensorGeo.clone();
      sensor.translate(side * 0.1, 0.11, -0.55);
      parts.push(sensor);
    }

    return this.mergeAndDispose(parts);
  }

  private mergeSupportGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];

    const hexGeo = new THREE.CylinderGeometry(0.5, 0.45, 0.35, 6);
    hexGeo.rotateX(Math.PI / 2);
    hexGeo.rotateZ(Math.PI / 2);
    parts.push(hexGeo);

    const deckGeo = new THREE.BoxGeometry(0.9, 0.1, 0.42);
    deckGeo.translate(0, 0.18, 0.05);
    parts.push(deckGeo);

    const coreGeo = new THREE.CylinderGeometry(0.19, 0.19, 0.5, 8);
    coreGeo.rotateX(Math.PI / 2);
    coreGeo.rotateZ(Math.PI / 2);
    parts.push(coreGeo);

    const rotorGeo = new THREE.TorusGeometry(0.28, 0.065, 8, 16);
    rotorGeo.rotateX(Math.PI / 2);
    const leftRotor = rotorGeo.clone();
    leftRotor.translate(-0.65, 0, 0);
    parts.push(leftRotor);
    const rightRotor = rotorGeo.clone();
    rightRotor.translate(0.65, 0, 0);
    parts.push(rightRotor);

    const antennaGeo = new THREE.CylinderGeometry(0.025, 0.025, 0.55, 6);
    antennaGeo.translate(0, 0.48, 0.18);
    parts.push(antennaGeo);

    const haloGeo = new THREE.TorusGeometry(0.36, 0.025, 8, 18);
    haloGeo.rotateX(Math.PI / 2);
    haloGeo.translate(0, 0.5, 0.18);
    parts.push(haloGeo);

    return this.mergeAndDispose(parts);
  }

  private mergeGuardianGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];

    const bodyGeo = new THREE.CylinderGeometry(0.46, 0.52, 0.48, 6);
    bodyGeo.rotateX(Math.PI / 2);
    bodyGeo.rotateZ(Math.PI / 6);
    parts.push(bodyGeo);

    const shieldGeo = new THREE.CylinderGeometry(0.62, 0.72, 0.18, 8, 1, false, 0, Math.PI);
    shieldGeo.rotateX(Math.PI / 2);
    shieldGeo.rotateZ(Math.PI);
    shieldGeo.translate(0, 0.02, -0.5);
    parts.push(shieldGeo);

    const shoulderGeo = new THREE.BoxGeometry(0.32, 0.18, 0.72);
    for (const side of [-1, 1]) {
      const shoulder = shoulderGeo.clone();
      shoulder.rotateZ(side * 0.16);
      shoulder.translate(side * 0.48, 0.02, 0.02);
      parts.push(shoulder);
    }

    const reactorGeo = new THREE.TorusGeometry(0.24, 0.045, 8, 16);
    reactorGeo.rotateX(Math.PI / 2);
    reactorGeo.translate(0, 0.04, 0.18);
    parts.push(reactorGeo);

    const rearEngineGeo = new THREE.CylinderGeometry(0.18, 0.24, 0.26, 10);
    rearEngineGeo.rotateX(Math.PI / 2);
    rearEngineGeo.translate(0, 0, 0.68);
    parts.push(rearEngineGeo);

    return this.mergeAndDispose(parts);
  }

  private mergeAndDispose(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
    const nonIndexed = parts.map((p) => (p.index ? p.toNonIndexed() : p));
    const merged = mergeGeometries(nonIndexed, false);
    merged.scale(4, 4, 4);
    for (const p of parts) p.dispose();
    for (const p of nonIndexed) {
      if (!parts.includes(p)) p.dispose();
    }
    return merged;
  }
}
